"use client";

import type { iCustomerPaymentMethod } from "@sdk/customer-payment-methods/customer-payment-method-model";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { LoadAllLedgers } from "@/store/modules/ledgers/ledgers.helpers";
import { selectAllLedgersQuery, selectLedgerById } from "@/store/modules/ledgers/ledgers.selectors";
import { useQueryWithStore } from "@/store/store.hooks";
import { Archive, Pencil, Wallet } from "lucide-react";
import { useSelector } from "react-redux";
import { cn } from "@/lib/utils";

export interface CustomerPaymentMethodCardProps {
  paymentMethod: iCustomerPaymentMethod;
  onEdit: () => void;
  onArchive?: () => void;
}

export function CustomerPaymentMethodCard({
  paymentMethod,
  onEdit,
  onArchive,
}: CustomerPaymentMethodCardProps) {
  useQueryWithStore(selectAllLedgersQuery, LoadAllLedgers, []);

  const ledger = useSelector(
    selectLedgerById(paymentMethod.connectedLedger ?? "")
  );

  const isArchived = paymentMethod.archived === true;
  const connectedLedgerId = paymentMethod.connectedLedger?.trim();

  const ledgerLine = connectedLedgerId
    ? `Ledger connected · ${ledger?.name?.trim() || connectedLedgerId}${
        ledger?.currency ? ` (${ledger.currency})` : ""
      }`
    : null;

  return (
    <Card
      className={cn(
        "w-[300px] flex flex-col transition-colors hover:bg-muted/50",
        isArchived && "opacity-70"
      )}
    >
      <CardHeader className="flex flex-row items-start justify-between space-y-0 p-4 pb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Wallet className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="font-semibold truncate">{paymentMethod.label}</span>
          {paymentMethod.defaultCurrency ? (
            <Badge variant="outline" className="shrink-0 text-xs font-normal">
              {paymentMethod.defaultCurrency}
            </Badge>
          ) : null}
          {isArchived ? (
            <Badge variant="secondary" className="shrink-0 text-xs">
              Archived
            </Badge>
          ) : null}
        </div>
        <div
          className="flex items-center gap-0.5 shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            aria-label="Edit payment method"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          {!isArchived && onArchive ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onArchive();
              }}
              aria-label="Archive payment method"
            >
              <Archive className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </CardHeader>
      {ledgerLine ? (
        <CardContent className="p-4 pt-0">
          <p className="text-sm text-muted-foreground truncate" title={ledgerLine}>
            {ledgerLine}
          </p>
        </CardContent>
      ) : null}
    </Card>
  );
}
